import React, { useEffect, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import { useSuperAdminTenant } from "../context/SuperAdminTenantContext";
import saApi from "../lib/saApi";
import { Zap, DollarSign, MessageSquare, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmt = (n) => (n ?? 0).toLocaleString();
const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function SAUsage() {
  const { tenantId } = useSuperAdminTenant();
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { days };
    if (tenantId && tenantId !== "all") params.tenant_id = tenantId;
    saApi.get("/usage", { params })
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [], total: { calls: 0, cost_usd: 0, input_tokens: 0, output_tokens: 0 } }))
      .finally(() => setLoading(false));
  }, [tenantId, days]);

  return (
    <SuperAdminShell title="AI Usage & Cost">
      <div className="px-5 md:px-8 py-6 space-y-6">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger data-testid="sa-usage-window" className="w-[140px] bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="AI Calls" value={fmt(data?.total?.calls)} icon={Zap} accent="#FBBF24" />
          <Kpi label="Cost" value={usd(data?.total?.cost_usd)} icon={DollarSign} accent="#34D399" />
          <Kpi label="Input Tokens" value={fmt(data?.total?.input_tokens)} icon={MessageSquare} accent="#22D3EE" />
          <Kpi label="Output Tokens" value={fmt(data?.total?.output_tokens)} icon={MessageSquare} accent="#A78BFA" />
        </div>

        <div className="rounded-2xl border border-white/10 overflow-hidden backdrop-blur-md" style={{ background: "rgba(255,255,255,0.02)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10 bg-white/5">
                <th className="px-4 py-3">Tenant</th>
                <th className="px-4 py-3 text-right">Calls</th>
                <th className="px-4 py-3 text-right">Input</th>
                <th className="px-4 py-3 text-right">Output</th>
                <th className="px-4 py-3 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr><td colSpan={5} className="px-4 py-6 text-white/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              ) : (data?.items || []).length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40">No AI usage in window</td></tr>
              ) : data.items.map((i) => (
                <tr key={i.tenant_id} data-testid="sa-usage-row" className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-4 py-2 text-white">{i.tenant_name}</td>
                  <td className="px-4 py-2 text-right text-white/80">{fmt(i.calls)}</td>
                  <td className="px-4 py-2 text-right text-white/60 text-xs">{fmt(i.input_tokens)}</td>
                  <td className="px-4 py-2 text-right text-white/60 text-xs">{fmt(i.output_tokens)}</td>
                  <td className="px-4 py-2 text-right text-emerald-300">{usd(i.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SuperAdminShell>
  );
}

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-2xl p-5 border border-white/10 backdrop-blur-md"
         style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))" }}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-white/50">{label}</div>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}22` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
      </div>
      <div className="mt-3 text-2xl font-display text-white">{value}</div>
    </div>
  );
}
